import type { CompanyProfileDto } from '@/api/companies'
import { CompanyAvatar } from '@/components/common/Avatar'
import { toSafeUrl } from './companyLinks'
import { LinkedInGlyph, GlobeGlyph } from './companyGlyphs'

/**
 * The company, as one line. Deliberately not a hero: the page's subject is the people below it,
 * and a large company header would promise a completeness a workspace-scoped roster cannot deliver.
 */
export function CompanyIdentity({ profile }: { profile: CompanyProfileDto }) {
  const facts = [profile.PrimaryIndustry, profile.StaffCountRange, profile.Headquarters]
    .filter(Boolean)
    .join(' · ')

  return (
    <div class="flex items-start gap-3.5 px-5 pt-5 pb-4">
      <CompanyAvatar companyName={profile.Name || 'Unknown'} logoUrl={profile.LogoUrl} size="lg" />

      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2">
          <h1 class="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100 leading-tight">
            {profile.Name || 'Unknown company'}
          </h1>
          {!profile.HasCompanyRecord && (
            <span
              class="inline-flex items-center rounded-md bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 text-2xs font-medium text-amber-800 dark:text-amber-300"
              title="No enriched company record — showing what this workspace's own records know"
            >
              Limited company detail
            </span>
          )}
        </div>
        {facts && <div class="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{facts}</div>}
        {profile.Tagline && (
          <div class="mt-1 text-sm text-slate-600 dark:text-slate-300">{profile.Tagline}</div>
        )}
      </div>

      <div class="flex flex-none gap-1.5 pt-0.5">
        {profile.LinkedInUrl && <ExternalLink href={profile.LinkedInUrl} label="LinkedIn" icon="linkedin" />}
        {profile.Website && <ExternalLink href={profile.Website} label="Website" icon="globe" />}
      </div>
    </div>
  )
}

/**
 * The Companies grids have always linked out to LinkedIn and the website. Making the company name
 * an internal link must not cost the user those — dropped affordances are how the last two
 * flyout-to-route migrations went wrong.
 */
function ExternalLink({ href, label, icon }: { href: string; label: string; icon: 'linkedin' | 'globe' }) {
  const safe = toSafeUrl(href)
  if (!safe) return null

  return (
    <a
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
      class="w-7.5 h-7.5 inline-grid place-items-center rounded-md border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
    >
      {icon === 'linkedin' ? <LinkedInGlyph /> : <GlobeGlyph />}
    </a>
  )
}
